<rt-box variant="vertical">
  <rt-heading variant="heading large" margin-bottom-small-x>
    {{ 'app.pricing.billing_page.billing' | translate }}
  </rt-heading>

  <!-- plan -->
  <section>
    <div layout-horizontal layout-space-between>
      <div layout-vertical>
        <rt-heading>
          {{ 'app.pricing.billing_page.plan' | translate }}
        </rt-heading>
        <div layout-horizontal>
          <span>{{ productName }}</span>
          <rt-divider variant="dot"></rt-divider>
          {{ intervalText }}

          @if (subscriptionStatus.blocked) {
            <rt-badge
              color="optional"
              [text]="'app.common.blocked' | translate"
              margin-left-small
              layout-align-self-center
            >
            </rt-badge>
          }

          @if (subscriptionStatus.paused) {
            <rt-badge
              color="counter-notification"
              [text]="'app.common.paused' | translate"
              margin-left-small
              layout-align-self-center
            >
            </rt-badge>
          }

          @if (subscriptionStatus.future) {
            <rt-badge
              color="optional"
              [text]="'app.common.scheduled' | translate"
              margin-left-small
              layout-align-self-center
            >
            </rt-badge>
          }

          @if (showCancelledBadge) {
            <rt-badge
              color="counter-notification"
              [text]="
                'app.pricing.billing_page.subscription_cancelled_label' | translate | uppercase
              "
              margin-left-small
              layout-align-self-center
            >
            </rt-badge>
          }

          @if (showNonRenewingBadge) {
            <rt-badge
              color="counter-notification"
              [text]="
                'app.pricing.billing_page.subscription_non_renewing_label' | translate | uppercase
              "
              margin-left-small
              layout-align-self-center
            >
            </rt-badge>
          }

          @if (!subscriptionStatus.canceled && trial) {
            <rt-divider variant="dot"></rt-divider>
            <span
              text-primary
              [innerHTML]="
                'app.pricing.billing_page.ongoing_trial'
                  | translate
                    : {
                        product: trial.productCode | titlecase,
                        timeLeft: trial.untilDate | rtTimeAgo
                      }
              "
            >
            </span>
          }

          @if (isPP24 && isStartPlan) {
            <rt-divider variant="dot"></rt-divider>
            {{ 'app.pricing.billing_page.pp24_slots_total' | translate: { total: slotsTotal } }}
            <rt-divider variant="dot"></rt-divider>
            <span [attr.text-red]="slotsTotal === jobSlotsUsed ? true : null">
              {{ 'app.pricing.billing_page.slots_used' | translate: { used: jobSlotsUsed } }}
            </span>
          }
        </div>
      </div>
      <div layout flex-0 layout-align-items-center>
        @if (showContactUsButton) {
          <rt-button variant="primary" (click)="openIntercomWithLegacyMessage()">
            {{ 'app.common.contact_us' | translate }}
          </rt-button>
        } @else {
          @if (!showNonRenewingBadge) {
            <rt-button (click)="editPlan.emit()" variant="primary">
              {{ 'app.pricing.billing_page.change_plan' | translate }}
            </rt-button>
          } @else {
            @if (showRenewButton) {
              <rt-button
                (click)="reactivatePlan.emit()"
                [loading]="reactivatePlanLoading"
                [disabled]="reactivatePlanLoading"
                variant="primary"
              >
                {{ 'app.pricing.billing_page.reactivate_plan' | translate }}
              </rt-button>
            }
          }
        }
      </div>
    </div>
  </section>

  <!-- slots -->
  @if (!isPP24) {
    <rt-divider margin-tb-small-x></rt-divider>
    <section>
      <div layout-horizontal layout-space-between>
        <div layout-vertical>
          <rt-heading>{{ 'app.pricing.billing_page.job_slots' | translate }}</rt-heading>
          <div layout-horizontal>
            @if (isUnlimited) {
              {{ 'app.pricing.billing_page.unlimited' | translate }}
              <rt-divider variant="dot"></rt-divider>
              {{ 'app.pricing.billing_page.slots_used' | translate: { used: jobSlotsUsed } }}
            }
            @if (!isUnlimited) {
              {{ 'app.pricing.billing_page.slots_total' | translate: { total: slotsTotal } }}
              <rt-divider variant="dot"></rt-divider>
              <span [attr.text-red]="slotsTotal === jobSlotsUsed ? true : null">
                {{
                  'app.pricing.billing_page.slots_status'
                    | translate: { used: jobSlotsUsed, available: slotsTotal - jobSlotsUsed }
                }}
              </span>
            }
          </div>
        </div>
        <div layout flex-0 layout-align-items-center>
          @if (showUpgradeSlotsButton) {
            <rt-button
              (click)="editSlots.emit()"
              [disabled]="isChangeSlotsButtonDisabled"
              variant="outline"
            >
              {{ 'app.pricing.billing_page.change_slots' | translate }}
            </rt-button>
          }
        </div>
      </div>
    </section>
  }

  <!-- sso -->
  @if (ssoAddon.isAvailable) {
    <rt-divider margin-tb-small-x></rt-divider>
    <section>
      <div layout-horizontal layout-space-between>
        <div layout-vertical>
          <rt-heading>{{ 'app.pricing.billing_page.sso_subscription' | translate }}</rt-heading>
          @if (ssoAddon.isActive) {
            <div>
              {{ 'app.pricing.billing_page.sso_subscription_active' | translate }}
            </div>
          }
          @if (!ssoAddon.isActive) {
            <div>
              {{ 'app.pricing.billing_page.sso_subscription_inactive' | translate }}
              <a (click)="goToSsoSettings()">
                {{ 'app.pricing.billing_page.go_to_sso_settings' | translate }}</a
              >
            </div>
          }
        </div>
        <div layout flex-0 layout-align-items-center>
          <rt-button (click)="ssoCancelSubscription()" variant="outline">
            {{ 'app.pricing.billing_page.sso_cancel_subscription' | translate }}
          </rt-button>
        </div>
      </div>
    </section>
  }

  <!-- texting -->
  @if (textingAddon) {
    <rt-divider margin-tb-small-x></rt-divider>
    <section>
      <div layout-horizontal layout-space-between>
        <div layout-vertical>
          <rt-heading>{{ 'app.pricing.billing_page.texting_subscription' | translate }}</rt-heading>
          @if (isTextingSubscriptionInactive) {
            <div>
              {{ 'app.pricing.billing_page.texting_subscription_inactive' | translate }}
            </div>
          }
          @if (!isTextingSubscriptionInactive) {
            <div layout-horizontal>
              {{
                'app.pricing.billing_page.texting_total_credits'
                  | translate: { totalCredits: textingAddon.totalCredits }
              }}
              <rt-divider variant="dot"></rt-divider>
              <span
                [attr.text-red]="textingCreditsLeft === 0 ? true : null"
                [attr.text-yellow]="lowCredits ? true : null"
                >{{
                  'app.pricing.billing_page.texting_subscription_details'
                    | translate
                      : {
                          creditsUsed:
                            textingCreditsLeft === 0
                              ? ('app.common.all' | translate)
                              : textingAddon.creditsUsed,
                          creditsLeft: textingCreditsLeft,
                          validUntil: textingAddon.validUntil | amDateFormat: 'MMMM DD'
                        }
                }}
              </span>
            </div>
          }
          @if (isTextingCancellationScheduled) {
            <span>{{
              'app.settings.texting.subscription.cancellation_scheduled' | translate
            }}</span>
          }
        </div>
        <div layout flex-0 layout-align-items-center>
          @if (!isTextingChangeSubscriptionButtonDisabled) {
            <rt-button (click)="textingSubscriptionChange.emit()" variant="outline">
              {{ 'app.pricing.billing_page.texting_change_subscription' | translate }}
            </rt-button>
          }
        </div>
      </div>
    </section>
  }

  <!-- next payment -->
  @if (nextPayment) {
    <rt-divider margin-tb-small-x></rt-divider>
    <section layout-vertical>
      <rt-heading>
        {{ 'app.pricing.billing_page.next_invoice' | translate }}
      </rt-heading>
      <div layout-horizontal>
        @if (isNextPaymentDiscounted) {
          (<span [style.text-decoration]="'line-through'">
            {{ nextPaymentAmount | currency: nextPayment.currency }}</span
          >)
        }
        {{ nextPayment.amountDue | currency: nextPayment.currency }}
        <rt-divider variant="dot"></rt-divider>
        {{ nextPayment.date | amDateFormat: 'DD MMMM YYYY' }}
        (<rt-date [time]="nextPayment.date"></rt-date>)
      </div>
      @if (nextPaymentVat) {
        <div layout-vertical>
          ({{ 'app.pricing.billing_page.includes_vat' | translate }} {{ nextPaymentVat.rate }}
          {{ nextPaymentVat.name }})
        </div>
      }
    </section>
  }

  @if (showScheduledChanges) {
    <rt-divider margin-tb-small-x></rt-divider>
    <section layout-vertical></section>
    <rt-billing-scheduled-change
      [scheduledChange]="scheduledChange!"
      [contractEndDate]="contractEnd"
      [amount]="
        ((nextPayment!.paysVat ? nextPayment!.subTotal : nextPayment!.total)
          | currency: nextPayment!.currency)!
      "
    ></rt-billing-scheduled-change>
  }

  @if (subscriptionStatus.canceled && validUntilDaysLeft <= 60) {
    <rt-message variant="context-error" margin-top>
      <div
        [innerHTML]="
          'app.pricing.billing_page.subscription_cancelled_message'
            | translate
              : {
                  timeLeft: 'app.common.x_days_plural' | translate: { count: validUntilDaysLeft }
                }
        "
      ></div>
    </rt-message>
  }
</rt-box>
